import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {ThemeContext} from 'src/apiContexts';
import SearchInput from 'src/autobahn/components/search-input';
import styles from './link-devices.module.scss';
import sharedStyles from './shared-styles.module.scss';
import Button from 'src/autobahn/components/button/button';
import {useDevices} from '../devices/queries';
import {useGroups} from '../groups/queries';
import {createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, useReactTable} from '@tanstack/react-table';
import TablePageControls from 'src/autobahn/components/table-page-controls';
import Group from 'src/core/models/group';
import * as Switch from '@radix-ui/react-switch';
import Yup from 'src/infra/Yup';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {usePermissionsByOwnerIdOrPropertyId, useUpdateDeviceToMaintenancePermissionsBulk} from '../permissions/queries';
import Table from 'src/autobahn/components/table';
import {useIntl} from 'react-intl';

type LinkDeviceTableData = {
  id: number;
  plate: string;
  model: string;
  group: string;
}

interface LinkDeviceProps {
  selectedMaintenance: number; // maintenance.id
  onCancel: () => void;
}

const columnHelper = createColumnHelper<LinkDeviceTableData>();

const pageSizeOptions = {
  'ten': 10,
  'fifty': 50,
  'all': -1,
} as const;

const schema = Yup.object().shape({
  items: Yup.array().of(Yup.object().shape({
    deviceId: Yup.number().required('Device ID is required'),
    maintenanceId: Yup.number().required('Maintenance ID is required'),
    linked: Yup.boolean().required('Linked status is required'),
  })),
});

const LinkDevice = ({selectedMaintenance, onCancel}: LinkDeviceProps) => {
  const {
    data: maintenanceLinkedDevices,
    isLoading: isMaintenanceLinkedDevicesLoading,
  } = usePermissionsByOwnerIdOrPropertyId({ownerClass: 'device', propertyClass: 'maintenance', propertyId: selectedMaintenance});

  const intl = useIntl();
  const theme = useContext(ThemeContext);

  const formMaintenanceLinkedDevices = useMemo(() => {
    return maintenanceLinkedDevices.reduce((accu, item) => {
      accu[`device_${item.deviceId}`] = true;
      return accu;
    }, {} as Record<string, boolean>);
  }, [maintenanceLinkedDevices]);

  const formRef = useRef(null);
  const [filterText, setFilterText] = useState('');

  const {control, handleSubmit, getValues, reset, formState: {touchedFields, dirtyFields}} = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      items: [],
    },
  });

  useFieldArray({
    control,
    name: 'items',
  });

  const {data: devices, isLoading: isDevicesLoading} = useDevices();
  const {data: groups, isLoading: isGroupsLoading} = useGroups();

  const loading = isDevicesLoading || isGroupsLoading || isMaintenanceLinkedDevicesLoading;

  const groupMap = useMemo(() => groups.reduce((accu, group) => {
    accu[group.id] = group;
    return accu;
  }, {} as Record<string, Group>), [groups]);

  const tableData = useMemo(() => {
    return devices.map((device) => ({
      id: device.id,
      plate: (device.attributes?.plate as string) || '-',
      model: device.attributes?.vehicleModel || '-',
      group: groupMap[device.groupId]?.name || '-',
    }));
  }, [devices, groupMap]);

  const tableDataForm = useMemo(() => tableData.map((item) => ({
    deviceId: item.id,
    maintenanceId: selectedMaintenance,
    linked: Boolean(formMaintenanceLinkedDevices[`device_${item.id}`]),
  })), [selectedMaintenance, tableData, formMaintenanceLinkedDevices]);

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'action',
      cell: ({row}) => {
        return (
          <Controller
            control={control}
            defaultValue={false}
            disabled={loading}
            name={`items.${row.index}.linked`}
            render={({field: {onChange, onBlur, value, ...fieldRest}}) => (
              <Switch.Root
                className={styles.switch}
                {...fieldRest}
                checked={value}
                onBlur={onBlur}
                onCheckedChange={onChange}
              >
                <Switch.Thumb className={styles.thumb} />
              </Switch.Root>
            )}
          />
        );
      },
    }),
    columnHelper.accessor('plate', {
      header: intl.formatMessage({id: 'devicePlate'}),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('model', {
      header: intl.formatMessage({id: 'deviceModel'}),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('group', {
      header: 'Group',
      cell: (info) => info.getValue(),
    }),
  ], [control, loading]);

  const [pagination, setPagination] = useState<{pageIndex: number; pageSize: 10 | 50 | typeof Infinity}>({
    pageIndex: 0,
    pageSize: pageSizeOptions.ten,
  });

  const getActualPageSize = (selectedSize: number) => {
    return selectedSize === -1 ? tableData.length : selectedSize;
  };

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setFilterText,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: 'includesString',
    state: {
      pagination: {
        ...pagination,
        pageSize: getActualPageSize(pagination.pageSize),
      },
      globalFilter: filterText,
    },
  });

  const {mutate, isPending} = useUpdateDeviceToMaintenancePermissionsBulk();

  const submit = () => {
    if (formRef.current) {
      // @ts-ignore
      formRef.current.dispatchEvent(new Event('submit', {cancelable: true}));
    }
  };

  // @ts-ignore
  const onFormDataValid = (data) => {
    if (!touchedFields.items || touchedFields.items.length === 0) return;

    const changedPerms = touchedFields.items.reduce((accu, item, index) => {
      if (item === null) return accu;

      const currentFieldValue = data.items[index];

      if (currentFieldValue.linked) {
        accu.created.push({
          deviceId: currentFieldValue.deviceId,
          maintenanceId: currentFieldValue.maintenanceId,
        });
      } else {
        accu.deleted.push({
          deviceId: currentFieldValue.deviceId,
          maintenanceId: currentFieldValue.maintenanceId,
        });
      }

      return accu;
    }, {
      created: [] as {deviceId: number; maintenanceId: number}[],
      deleted: [] as {deviceId: number; maintenanceId: number}[],
    });

    mutate(changedPerms, {
      onSuccess: () => {
        onCancel();
      },
    });
  };

  // @ts-ignore
  const onFormDatInvalid = (data) => {
    console.log('formData_invalid', {
      data,
      touchedFields: touchedFields,
      dirtyFields: dirtyFields,
      values: getValues(),
    });
  };

  useEffect(() => {
    if (tableDataForm) {
      reset({
        items: tableDataForm,
      });
    }
  }, [tableDataForm, reset]);

  return (
      <div className={styles.container}>
        <div className={styles.actionBar}>
          <SearchInput onChange={table.setGlobalFilter} />
        </div>

        <form ref={formRef} onSubmit={handleSubmit(onFormDataValid, onFormDatInvalid)}>
          <Table
            table={table}
            theme={theme}
            emptyMessage={intl.formatMessage({id: 'sharedNoDataText'})}
          />
        </form>

        <TablePageControls
          pageCount={table.getPageCount()}
          pageIndex={table.getState().pagination.pageIndex}
          pageSize={table.getState().pagination.pageSize}
          onPageSizeChange={(newPageSize) => {
            if (newPageSize === -1) {
              table.setPageSize(tableData.length);
              setPagination((prev) => ({...prev, pageSize: -1}));
            } else {
              table.setPageSize(newPageSize);
              setPagination((prev) => ({...prev, pageSize: newPageSize}));
            }
          }}
          setPageIndex={table.setPageIndex}
          getCanPreviousPage={table.getCanPreviousPage}
          getCanNextPage={table.getCanNextPage}
          previousPage={table.previousPage}
          nextPage={table.nextPage}
        />

        <div className={sharedStyles.buttonsContainer}>
          <Button disabled={isPending} variant='secondary' onClick={onCancel}>
            <span>Cancel</span>
          </Button>
          <Button disabled={isPending} onClick={submit}>
            <span>Submit</span>
          </Button>
        </div>
      </div>
    );
};

export default LinkDevice;
